import { useEffect, useRef, useState } from "react";
import type { Question } from "./types";

/**
 * Per-question progress strip. Single line, but horizontally scrollable
 * when there are too many chips to fit. The active chip is auto-scrolled
 * into view on every change.
 */
export function PartProgress({
  questions,
  current,
  isAnswered,
  isGrading,
  scoreFor,
  adFor,
  showScores,
  frontier,
  onJump,
  compact,
}: {
  questions: Question[];
  current: number;
  isAnswered?: (i: number) => boolean;
  isVisited?: (i: number) => boolean;
  isGrading?: (i: number) => boolean;
  scoreFor?: (i: number) => number | null;
  adFor?: (i: number) => number | null;
  showScores?: boolean;
  frontier?: number;
  onJump?: (i: number) => void;
  /**
   * Compact dot strip for the slim exam top bar: small dots (done=pass,
   * current=accent, todo=border) + a "Deel N · i/total" label that hides
   * below `lg`. No per-question jumping — this is a status indicator.
   */
  compact?: boolean;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLLIElement | null>(null);
  const effectiveFrontier = frontier ?? Number.POSITIVE_INFINITY;
  const [isOverflowing, setIsOverflowing] = useState(false);

  if (compact) {
    const active = questions[current];
    const partLabel = active
      ? `Deel ${active.part} · ${current + 1}/${questions.length}`
      : `${current + 1}/${questions.length}`;
    return (
      <div
        className="flex items-center gap-2"
        aria-label="Voortgang per vraag"
        role="group"
      >
        <ol className="flex items-center gap-1.5">
          {questions.map((q, i) => {
            const answered = isAnswered ? isAnswered(i) : i < current;
            const isActive = i === current;
            const dotClass = isActive
              ? "bg-[var(--accent)]"
              : answered
                ? "bg-[var(--pass)]"
                : "bg-[var(--border-strong)]";
            return (
              <li
                key={q.id}
                className={`h-2 w-2 rounded-full ${dotClass}`}
                aria-current={isActive ? "step" : undefined}
              />
            );
          })}
        </ol>
        <span className="hidden lg:inline text-[13px] font-semibold text-[var(--ink-muted)]">
          {partLabel}
        </span>
      </div>
    );
  }

  useEffect(() => {
    const el = activeRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [current]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [questions.length]);

  return (
    <div
      ref={scrollerRef}
      className="overflow-x-auto -mx-2 px-2 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sticky top-0 z-10 bg-[var(--bg)] lg:static lg:bg-transparent"
      aria-label="Voortgang per vraag"
      data-overflow={isOverflowing ? "true" : undefined}
      style={
        isOverflowing
          ? {
              maskImage:
                "linear-gradient(to right, black 92%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, black 92%, transparent 100%)",
            }
          : undefined
      }
    >
      <ol className="flex gap-1.5 items-center font-mono-badge text-xs whitespace-nowrap">
        {questions.map((q, i) => {
          const answered = isAnswered ? isAnswered(i) : i < current;
          const grading = isGrading?.(i) ?? false;
          const isActive = i === current;
          const beyondFrontier = i > effectiveFrontier;
          const showPartLabel = i === 0 || q.part !== questions[i - 1]?.part;
          const canJump = !!onJump && !isActive && !beyondFrontier;
          const baseDotClasses =
            "inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors";
          const score = showScores && answered ? (scoreFor?.(i) ?? null) : null;
          const ad = showScores && answered ? (adFor?.(i) ?? null) : null;
          const incomplete = ad !== null && ad < 2 && score !== null && score >= 8;
          const scoreBand: "pass" | "warn" | "fail" | null =
            score === null
              ? null
              : incomplete
                ? "warn"
                : score >= 10
                  ? "pass"
                  : score <= 7
                    ? "fail"
                    : "warn";
          const scoreDotClasses =
            scoreBand === "pass"
              ? "bg-[var(--pass)]/15 border border-[var(--pass)] text-[var(--pass)]"
              : scoreBand === "warn"
                ? "bg-[var(--warn)]/15 border border-[var(--warn)] text-[var(--warn)]"
                : scoreBand === "fail"
                  ? "bg-[var(--fail)]/15 border border-[var(--fail)] text-[var(--fail)]"
                  : "";
          const stateDotClasses = beyondFrontier
            ? "border border-[var(--border)] text-[var(--ink-faint)] opacity-50"
            : isActive
              ? "bg-[var(--accent-soft)] border border-[var(--accent)] text-[var(--ink)]"
              : scoreBand !== null
                ? scoreDotClasses
                : answered
                  ? "bg-[var(--panel)] border border-[var(--border)] text-[var(--ink-muted)]"
                  : "border border-[var(--border)] text-[var(--ink-faint)]";
          const interactiveDotClasses =
            "cursor-pointer hover:border-[var(--accent)] hover:text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2";
          const dotContent = answered ? "✓" : q.questionIdx + 1;
          const scoreSuffix = score !== null ? `, score ${score} van 12` : "";
          const ariaLabel = answered
            ? `Bekijk beurt D${q.part}.${q.questionIdx + 1}${scoreSuffix}`
            : `Beantwoord beurt D${q.part}.${q.questionIdx + 1}`;
          return (
            <li
              key={q.id}
              ref={isActive ? activeRef : null}
              className="flex items-center gap-1.5 shrink-0"
              aria-current={isActive ? "step" : undefined}
            >
              {showPartLabel ? (
                <span
                  className={`px-1.5 py-0.5 rounded-sm text-xs uppercase tracking-wider ${
                    isActive ? "text-[var(--accent)]" : "text-[var(--ink-faint)]"
                  }`}
                >
                  D{q.part}
                </span>
              ) : null}
              <span className="relative inline-flex">
                {canJump ? (
                  <button
                    type="button"
                    onClick={() => onJump(i)}
                    className={`${baseDotClasses} ${stateDotClasses} ${interactiveDotClasses}`}
                    aria-label={ariaLabel}
                  >
                    {dotContent}
                  </button>
                ) : (
                  <span className={`${baseDotClasses} ${stateDotClasses}`}>
                    {dotContent}
                  </span>
                )}
                {grading ? (
                  <span aria-hidden="true" className="chip-grading-ring" />
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
